import { useEffect, useState } from 'react'
import { AlertTriangle, CheckCircle2, Clock3 } from 'lucide-react'

import { supabase } from '../lib/supabase'

interface ManagerBudgetStatusCardProps {
  teamId: string
  podId?: string
}

interface BudgetStatus {
  podId: string
  name: string
  limit: number | null
  spend: number
}

const statusFor = (budget: BudgetStatus) => {
  if (budget.limit === null) return { label: 'No budget set', className: 'text-muted-foreground', Icon: Clock3 }
  const percent = (budget.spend / budget.limit) * 100
  if (percent >= 100) return { label: 'Over budget', className: 'text-destructive', Icon: AlertTriangle }
  if (percent >= 80) return { label: 'Approaching limit', className: 'text-amber-600', Icon: AlertTriangle }
  return { label: 'Healthy', className: 'text-emerald-600', Icon: CheckCircle2 }
}

export function ManagerBudgetStatusCard({ teamId, podId = 'all' }: ManagerBudgetStatusCardProps) {
  const [budgets, setBudgets] = useState<BudgetStatus[]>([])
  const [loadedAt, setLoadedAt] = useState<Date | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError('')

      try {
        const { data: pods, error: podsError } = await (supabase.from as any)('pods')
          .select('id, name')
          .eq('team_id', teamId)
          .is('archived_at', null)
          .order('name')
        if (podsError) throw podsError

        const visiblePods = ((pods ?? []) as { id: string; name: string }[])
          .filter((pod) => podId === 'all' || pod.id === podId)

        if (visiblePods.length === 0) {
          if (!cancelled) {
            setBudgets([])
            setLoading(false)
          }
          return
        }

        const podIds = visiblePods.map((pod) => pod.id)
        const [{ data: thresholds, error: thresholdError }, { data: spendRows, error: spendError }] = await Promise.all([
          (supabase.from as any)('budget_thresholds')
            .select('pod_id, monthly_limit_usd')
            .in('pod_id', podIds),
          (supabase.from as any)('agent_usage_events')
            .select('cost_usd, agent:agents!agent_usage_events_agent_id_fkey(user_id, user:users!agents_user_id_fkey(pod_id))')
            .eq('team_id', teamId)
            .gte('usage_date', new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), 1)).toISOString().slice(0, 10))
            .lte('usage_date', new Date().toISOString().slice(0, 10))
            .not('cost_usd', 'is', null),
        ])
        if (thresholdError) throw thresholdError
        if (spendError) throw spendError

        const limitByPod = new Map<string, number>((thresholds ?? []).map((row: any) => [row.pod_id, Number(row.monthly_limit_usd)]))
        const spendByPod = new Map<string, number>()
        for (const row of spendRows ?? []) {
          const agent = Array.isArray(row.agent) ? row.agent[0] : row.agent
          const owner = agent?.user && Array.isArray(agent.user) ? agent.user[0] : agent?.user
          if (owner?.pod_id) {
            spendByPod.set(owner.pod_id, (spendByPod.get(owner.pod_id) ?? 0) + Number(row.cost_usd ?? 0))
          }
        }

        if (!cancelled) {
          setBudgets(visiblePods.map((pod) => ({
            podId: pod.id,
            name: pod.name,
            limit: limitByPod.get(pod.id) ?? null,
            spend: spendByPod.get(pod.id) ?? 0,
          })))
          setLoadedAt(new Date())
        }
      } catch (loadError: any) {
        if (!cancelled) setError(loadError.message || 'Could not load budget status')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [podId, teamId])

  return (
    <section className="border border-border bg-background p-4 shadow-sm sm:p-5" aria-label="Project budget status">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-foreground">Project budget status</h2>
          <p className="mt-1 text-sm text-muted-foreground">Estimated connector spend for your assigned projects this month.</p>
        </div>
        {loadedAt && <span className="text-xs text-muted-foreground">Updated {loadedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>}
      </div>

      {loading && <p className="mt-5 text-sm text-muted-foreground">Loading budget status…</p>}
      {error && <p role="alert" className="mt-5 text-sm text-destructive">{error}</p>}
      {!loading && !error && budgets.length === 0 && <p className="mt-5 text-sm text-muted-foreground">No assigned projects found.</p>}

      {!loading && !error && budgets.length > 0 && (
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          {budgets.map((budget) => {
            const status = statusFor(budget)
            const percent = budget.limit ? (budget.spend / budget.limit) * 100 : 0
            const StatusIcon = status.Icon
            return (
              <div key={budget.podId} className="border border-border p-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium text-foreground">{budget.name}</span>
                  <span className={`flex items-center gap-1 text-xs font-medium ${status.className}`}>
                    <StatusIcon className="h-3.5 w-3.5" />{status.label}
                  </span>
                </div>
                <p className="mt-2 text-sm text-muted-foreground">
                  {budget.limit === null
                    ? `$${budget.spend.toFixed(2)} estimated spend · Set a budget to track status`
                    : `$${budget.spend.toFixed(2)} of $${budget.limit.toFixed(2)} used · ${Math.round(percent)}%`}
                </p>
                {budget.limit !== null && (
                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(percent)} aria-label={`${budget.name} budget usage`}>
                    <div className={`h-full ${percent >= 100 ? 'bg-destructive' : percent >= 80 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${Math.min(percent, 100)}%` }} />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}
